import React, { useState, useEffect, useMemo } from "react";
import { Download, Copy, Check } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
  Badge,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  SpinnerLoader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  getDatastreamEventLogById,
  DatastreamEventLogDetail,
  EventType,
} from "../../../adverity-logs/api";

interface EventDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: number | null;
}

interface ParsedLogEntry {
  timestamp: string;
  level: string;
  message: string;
}

type LogRow = ParsedLogEntry & { _srNo: number };

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  callback_received: "Callback Received",
  backup_fetch_triggered: "Backup Fetch Triggered",
};

const TAB_KEYS = ["request", "response", "logs", "error"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const getLevelClasses = (level: string): string => {
  switch (level.toUpperCase()) {
    case "ERROR":
      return "bg-red-100 text-red-700 border border-red-300";
    case "WARNING":
    case "WARN":
      return "bg-amber-100 text-amber-700 border border-amber-300";
    case "INFO":
      return "bg-green-100 text-green-700 border border-green-300";
    case "DEBUG":
      return "bg-gray-100 text-gray-600 border border-gray-300";
    case "SUCCESS":
      return "bg-emerald-100 text-emerald-700 border border-emerald-300";
    default:
      return "bg-gray-100 text-gray-600 border border-gray-300";
  }
};

const getStatusClasses = (status: string): string => {
  switch (status) {
    case "success":
      return "bg-green-100 text-green-700 border-green-400";
    case "error":
      return "bg-red-100 text-red-700 border-red-400";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const EventDetailsDialog: React.FC<EventDetailsDialogProps> = ({
  open,
  onClose,
  eventId,
}) => {
  const [eventLog, setEventLog] = useState<DatastreamEventLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("request");
  const [copySuccess, setCopySuccess] = useState(false);
  const [parsedLogs, setParsedLogs] = useState<ParsedLogEntry[]>([]);

  useEffect(() => {
    if (open && eventId) {
      setActiveTab("request");
      fetchData();
    }
  }, [open, eventId]);

  const fetchData = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await getDatastreamEventLogById(eventId);
      setEventLog(data);
      parseLogs(data.log_entries);
    } catch (err) {
      console.error("Error fetching event log", err);
    } finally {
      setLoading(false);
    }
  };

  const parseLogs = (logEntries: string | null) => {
    if (!logEntries) {
      setParsedLogs([]);
      return;
    }

    const lines = logEntries.split("\n").filter((line) => line.trim());
    const parsed: ParsedLogEntry[] = lines.map((line) => {
      const match = line.match(
        /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},?\d*)\s*-\s*(\w+)\s*-\s*(.*)$/
      );
      if (match) {
        return { timestamp: match[1], level: match[2], message: match[3] };
      }
      return { timestamp: "", level: "INFO", message: line };
    });
    setParsedLogs(parsed);
  };

  const formatTime = (timestamp: string): string => {
    if (!timestamp) return "--:--:--";
    try {
      const normalized = timestamp.replace(",", ".").replace("T", " ");
      const date = new Date(normalized);
      if (isNaN(date.getTime())) {
        const timeMatch = timestamp.match(/(\d{2}:\d{2}:\d{2})/);
        return timeMatch ? timeMatch[1] : timestamp.substring(11, 19);
      }
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return timestamp.substring(11, 19) || "--:--:--";
    }
  };

  const getCurrentContent = (): string => {
    if (!eventLog) return "";
    switch (activeTab) {
      case "request":
        return eventLog.request_payload ? JSON.stringify(eventLog.request_payload, null, 2) : "";
      case "response":
        return eventLog.response_payload ? JSON.stringify(eventLog.response_payload, null, 2) : "";
      case "logs":
        return parsedLogs.map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`).join("\n");
      case "error":
        return eventLog.error_message || "";
      default:
        return "";
    }
  };

  const hasContent = (): boolean => {
    return getCurrentContent().length > 0;
  };

  const handleCopy = async () => {
    const content = getCurrentContent();
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  const handleDownload = () => {
    const content = getCurrentContent();
    if (!content) return;
    const tabFileMap: Record<TabKey, { name: string; ext: string; type: string }> = {
      request: { name: "request-payload", ext: "json", type: "application/json" },
      response: { name: "response-payload", ext: "json", type: "application/json" },
      logs: { name: "log-entries", ext: "txt", type: "text/plain" },
      error: { name: "error-message", ext: "txt", type: "text/plain" },
    };
    const info = tabFileMap[activeTab];
    const blob = new Blob([content], { type: info.type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `event-${eventId}-${info.name}-${new Date().toISOString().split("T")[0]}.${info.ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const logTableData = useMemo<LogRow[]>(
    () => parsedLogs.map((log, i) => ({ ...log, _srNo: i + 1 })),
    [parsedLogs]
  );

  const logColumns = useMemo((): TableColumn<LogRow>[] => [
    {
      key: "_srNo",
      header: "Sr No",
      accessor: (row) => row._srNo,
      width: "70px",
      render: (_, row) => <span className="text-gray-500">{row._srNo}</span>,
    },
    {
      key: "time",
      header: "Time",
      accessor: (row) => row.timestamp,
      width: "100px",
      render: (_, row) => <span className="text-gray-600 font-mono text-xs">{formatTime(row.timestamp)}</span>,
    },
    {
      key: "level",
      header: "Level",
      accessor: (row) => row.level,
      width: "90px",
      align: "center" as const,
      render: (_, row) => (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium uppercase whitespace-nowrap ${getLevelClasses(row.level)}`}>
          {row.level}
        </span>
      ),
    },
    {
      key: "message",
      header: "Message",
      accessor: (row) => row.message,
      render: (_, row) => (
        <span className="text-gray-700 text-xs break-words whitespace-pre-wrap">{row.message}</span>
      ),
    },
  ], []);

  const jsonBlock = (content: any, emptyMsg: string) => {
    if (!content) {
      return (
        <div className="flex items-center justify-center flex-1 py-16 text-gray-400 text-sm">
          {emptyMsg}
        </div>
      );
    }
    return (
      <pre className="flex-1 overflow-auto m-0 p-4 bg-[#1e1e1e] text-[#d4d4d4] font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words rounded-md">
        {typeof content === "string" ? content : JSON.stringify(content, null, 2)}
      </pre>
    );
  };

  const logsDataTable = (
    <div className="flex-1 flex flex-col min-h-0">
      <DataTable<LogRow>
        className="flex-1 min-h-0 overflow-auto"
        maxHeight="100%"
        columns={logColumns}
        data={logTableData}
        loading={false}
        emptyMessage="No log entries available."
        stickyHeader
        stickyFooter
        showActions={false}
      />
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side="right"
        className="w-[900px] sm:max-w-[900px] flex flex-col h-full overflow-y-auto"
      >
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Event Details</SheetTitle>
            <div className="flex items-center gap-2">
              {eventLog && (
                <>
                  <Badge variant="outline" className="text-xs">
                    {EVENT_TYPE_LABELS[eventLog.event_type] || eventLog.event_type}
                  </Badge>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize border ${getStatusClasses(eventLog.status)}`}>
                    {eventLog.status}
                  </span>
                  {eventLog.datastream_id && (
                    <Badge variant="secondary" className="text-xs">
                      DS: {eventLog.datastream_id}
                    </Badge>
                  )}
                </>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={handleCopy} disabled={!hasContent()} className="h-8 w-8">
                      {copySuccess ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>{copySuccess ? "Copied!" : "Copy to Clipboard"}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={handleDownload} disabled={!hasContent()} className="h-8 w-8">
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Download</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center flex-1 p-8">
            <SpinnerLoader className="h-8 w-8" />
          </div>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as TabKey)}
            className="flex flex-1 flex-col min-h-0 mt-4"
          >
            <TabsList className="grid grid-cols-4 w-fit mb-4">
              <TabsTrigger value="request" className="flex items-center gap-1.5">
                Request
                {eventLog?.request_payload && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
              </TabsTrigger>
              <TabsTrigger value="response" className="flex items-center gap-1.5">
                Response
                {eventLog?.response_payload && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
              </TabsTrigger>
              <TabsTrigger value="logs" className="flex items-center gap-1.5">
                Logs
                {parsedLogs.length > 0 && (
                  <Badge variant="secondary" className="h-4 px-1.5 text-[10px] leading-none">{parsedLogs.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="error" className="flex items-center gap-1.5">
                Error
                {eventLog?.error_message && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="request" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
              {jsonBlock(eventLog?.request_payload, "No request payload available")}
            </TabsContent>

            <TabsContent value="response" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
              {jsonBlock(eventLog?.response_payload, "No response payload available")}
            </TabsContent>

            <TabsContent value="logs" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
              {logsDataTable}
            </TabsContent>

            <TabsContent value="error" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
              {eventLog?.error_message ? (
                <div className="flex-1 overflow-auto p-4 bg-red-50 rounded-md">
                  <pre className="m-0 p-4 bg-white border border-red-200 rounded-md font-mono text-[13px] text-red-800 leading-relaxed whitespace-pre-wrap break-words">
                    {eventLog.error_message}
                  </pre>
                </div>
              ) : (
                <div className="flex items-center justify-center flex-1 py-16 text-gray-400 text-sm">
                  No error message - event completed successfully
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <SheetFooter className="border-t pt-4 mt-4 flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            {eventLog && (
              <span className="text-xs text-gray-400">
                Event ID: {eventId} | Created: {new Date(eventLog.created_at).toLocaleString()}
              </span>
            )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EventDetailsDialog;
